import * as React from "react";
import { StyleSheet, View } from "@-/com.npmjs.react-native";

import type { PersistentKeyboardViewProps } from "./PersistentKeyboard.types.ts";
import {
  emitChangeText,
  emitKeyPress,
  getNativeEventCount,
  registerWebInputHandle,
  syncWebSelection,
} from "./PersistentKeyboardModule.web";

/** Matches the iOS/Android placeholder font — the one family the app ships. */
const PLACEHOLDER_FONT_FAMILY = "Han Composite";

/**
 * The placeholder color/font can't be expressed as inline style on a DOM
 * `<input>` (the `::placeholder` pseudo-element needs a stylesheet), so we
 * publish them as CSS custom properties on the element and reference them from
 * an injected rule. Each mounted input gets a unique class so concurrent
 * inputs don't clobber each other's vars.
 */
const PLACEHOLDER_STYLE_ID = "persistent-keyboard-placeholder-style";
let placeholderStyleInjected = false;
function ensurePlaceholderStyleInjected(): void {
  if (placeholderStyleInjected || typeof document === "undefined") {
    return;
  }
  placeholderStyleInjected = true;
  const style = document.createElement("style");
  style.id = PLACEHOLDER_STYLE_ID;
  style.textContent = `.persistent-keyboard-input::placeholder {
    color: var(--persistent-keyboard-placeholder-color);
    font-family: var(--persistent-keyboard-placeholder-font);
    opacity: 1;
  }
  .persistent-keyboard-input--hide-placeholder-while-focused:focus::placeholder {
    color: transparent;
  }`;
  document.head.append(style);
}

const styles = StyleSheet.create({
  container: { alignItems: "center", flex: 1, flexDirection: "row" },
});

let nextWebInputRegistrationId = 0;

export default function PersistentKeyboardView(
  props: PersistentKeyboardViewProps,
): React.ReactElement | null {
  const {
    color,
    fontFamily,
    fontSize,
    hidePlaceholderWhileFocused = false,
    mostRecentEventCount = 0,
    placeholder,
    placeholderTextColor,
    text,
    textPaddingLeft,
  } = props;
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const registrationIdRef = React.useRef(++nextWebInputRegistrationId);

  React.useEffect(() => {
    ensurePlaceholderStyleInjected();
  }, []);

  React.useEffect(() => {
    const registrationId = registrationIdRef.current;
    registerWebInputHandle(
      {
        blur() {
          inputRef.current?.blur();
        },
        focus() {
          inputRef.current?.focus();
        },
        setSelection(selection) {
          inputRef.current?.setSelectionRange(selection.start, selection.end);
        },
        setText(next) {
          if (inputRef.current !== null && inputRef.current.value !== next) {
            inputRef.current.value = next;
          }
        },
      },
      registrationId,
    );
    return () => {
      registerWebInputHandle(null, registrationId);
    };
  }, []);

  React.useEffect(() => {
    if (text === undefined) {
      return;
    }
    // Same guard as the native modules: a `text` derived from a render older than the newest
    // input event would overwrite characters the `<input>` already holds. DOM events are
    // delivered synchronously so the window is far narrower than on native, but keeping the
    // rule identical means the platforms can't drift.
    if (mostRecentEventCount < getNativeEventCount()) {
      return;
    }
    if (inputRef.current !== null && inputRef.current.value !== text) {
      inputRef.current.value = text;
    }
  }, [mostRecentEventCount, text]);

  React.useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const resolvedFontSize = fontSize ?? 17;
  // The value is rendered (and edited) directly in the `<input>` so the native
  // text caret blinks at the cursor position. Using a transparent caret here —
  // as an earlier version did, with the value mirrored into an overlay
  // `<Text>` — left the focused field looking dead on web.
  const inputStyle: React.CSSProperties = {
    background: "transparent",
    border: "none",
    caretColor: color as string | undefined,
    color: color as string | undefined,
    fontFamily,
    fontSize: resolvedFontSize,
    height: "100%",
    lineHeight: `${resolvedFontSize}px`,
    margin: 0,
    outline: "none",
    paddingBottom: 0,
    paddingLeft: textPaddingLeft,
    paddingRight: 0,
    paddingTop: 0,
    WebkitTapHighlightColor: "transparent",
    width: "100%",
    // Custom properties consumed by the injected `::placeholder` rule.
    ["--persistent-keyboard-placeholder-color" as string]: placeholderTextColor as string,
    ["--persistent-keyboard-placeholder-font" as string]: PLACEHOLDER_FONT_FAMILY,
  };

  return (
    <View style={styles.container}>
      <input
        aria-label={placeholder}
        autoCapitalize="off"
        autoComplete="off"
        autoCorrect="off"
        className={
          hidePlaceholderWhileFocused
            ? "persistent-keyboard-input persistent-keyboard-input--hide-placeholder-while-focused"
            : "persistent-keyboard-input"
        }
        defaultValue={text ?? ""}
        onChange={(event) => {
          const input = event.currentTarget;
          emitChangeText(
            input.value,
            { start: input.selectionStart ?? 0, end: input.selectionEnd ?? 0 },
            (event.nativeEvent as InputEvent).isComposing,
          );
        }}
        onCompositionEnd={(event) => {
          const input = event.currentTarget;
          emitChangeText(input.value, {
            start: input.selectionStart ?? 0,
            end: input.selectionEnd ?? 0,
          });
        }}
        onSelect={(event) => {
          const input = event.currentTarget;
          syncWebSelection(input.value, {
            start: input.selectionStart ?? 0,
            end: input.selectionEnd ?? 0,
          });
        }}
        onKeyDown={(event) => {
          emitKeyPress(event.key);
        }}
        placeholder={placeholder}
        ref={inputRef}
        spellCheck={false}
        style={inputStyle}
        type="text"
      />
    </View>
  );
}
